import {useEffect, useRef, useState} from 'react';
import {BackendApi} from "../../../../../api/api.ts";
import {User} from "../../../../../api/accessControl/User.ts";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {isAbortError} from "../../../../../lib/abortError.ts";

const POS_RECEIPT_NOTIFICATION_MODE = 'POS_RECEIPT_NOTIFICATION_MODE';

export type PosReceiptNotificationMode = 'IMMEDIATE' | 'DIGEST' | 'OFF';

const DEFAULT_MODE: PosReceiptNotificationMode = 'IMMEDIATE';

const DELIVERY_MODES: { value: PosReceiptNotificationMode, label: string, description: string }[] = [
    {
        value: 'IMMEDIATE',
        label: 'Direct',
        description: 'Je krijgt een melding zodra we een pinbetaling zien, zodat je het bonnetje meteen kunt fotograferen.',
    },
    {
        value: 'DIGEST',
        label: 'Eén keer per dag',
        description: 'Elke dag om 18:00 één melding met alle bonnetjes die we nog missen.',
    },
    {
        value: 'OFF',
        label: 'Uit',
        description: 'Je krijgt geen meldingen over pinbetalingen. Je bonnetjes blijven zichtbaar in de app.',
    },
];

interface Props {
    currentUser: User;
}

export function NotificationSettingsTab({currentUser}: Props) {
    const [mode, setMode] = useState<PosReceiptNotificationMode | null>(null);
    const [saving, setSaving] = useState(false);
    const {dispatch} = useNotifications();

    const savedMode = useRef<PosReceiptNotificationMode>(DEFAULT_MODE);
    const savingRef = useRef(false);

    useEffect(() => {
        const controller = new AbortController();

        async function loadSettings(signal: AbortSignal) {
            try {
                const api = new BackendApi(currentUser.getToken()).withAbortSignal(signal);
                const body = await api.Me__settings(null);
                const settings = body.settings as Record<string, string | null>;
                const stored = (settings?.[POS_RECEIPT_NOTIFICATION_MODE] as PosReceiptNotificationMode)
                    ?? DEFAULT_MODE;

                savedMode.current = stored;
                setMode(stored);
            } catch (error) {
                if (isAbortError(error)) {
                    return;
                }
                setMode(DEFAULT_MODE);
                dispatch({
                    title: "Instelling niet geladen",
                    description: "Je meldingsvoorkeur kon niet worden opgehaald. Probeer het later opnieuw.",
                    type: "error",
                });
            }
        }

        void loadSettings(controller.signal);
        return () => controller.abort();
    }, [currentUser, dispatch]);

    async function changeMode(value: PosReceiptNotificationMode) {
        if (savingRef.current) {
            return;
        }

        const previous = savedMode.current;
        savingRef.current = true;
        setMode(value);
        setSaving(true);

        try {
            await new BackendApi(currentUser.getToken()).Me__settings__changeSetting({
                setting_name: POS_RECEIPT_NOTIFICATION_MODE,
                setting_value: value,
            });

            savedMode.current = value;
        } catch {
            setMode(previous);
            dispatch({
                title: "Instelling niet opgeslagen",
                description: "Je meldingsvoorkeur kon niet worden opgeslagen. Probeer het opnieuw.",
                type: "error",
            });
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }

    if (mode === null) {
        return <div className="py-6 text-sm text-gray-500">Instellingen laden…</div>;
    }

    return (
        <div>
            <h3 className="text-xl font-gray-900 py-3 pt-6">Meldingen over pinbetalingen</h3>
            <p className="text-sm text-gray-600 mb-4 max-w-2xl">
                Wanneer je met je pas of telefoon betaalt, kunnen we je vragen het bonnetje te uploaden.
                Meldingen komen aan op de Pocket CFO app op je telefoon.
            </p>

            <fieldset className="space-y-3" disabled={saving}>
                <legend className="sr-only">Wanneer wil je een melding krijgen?</legend>

                {DELIVERY_MODES.map(option => (
                    <label
                        key={option.value}
                        className={
                            'flex gap-x-3 bg-gray-50 p-3 rounded-md cursor-pointer ring-1 ' +
                            (mode === option.value ? 'ring-primary' : 'ring-transparent')
                        }
                    >
                        <input
                            type="radio"
                            name="pos-receipt-notification-mode"
                            className="mt-1 h-4 w-4 shrink-0 text-primary focus:ring-primary"
                            value={option.value}
                            checked={mode === option.value}
                            onChange={() => changeMode(option.value)}
                        />
                        <span>
                            <span className="block font-medium text-gray-900">{option.label}</span>
                            <span className="block text-sm text-gray-600">{option.description}</span>
                        </span>
                    </label>
                ))}
            </fieldset>
        </div>
    );
}
